import { WebAppPersonDto } from '@mi-front/api-v2';
import PersonIcon from '@mui/icons-material/Person';
import { Avatar, AvatarProps } from '@mui/material';

type PersonAvatarSize = 'small' | 'medium' | 'large';

const SIZE_PX: Record<PersonAvatarSize, number> = {
  small: 20,
  medium: 28,
  large: 40,
};

type PersonAvatarProps = {
  /**
   * Person DTO when resolved. When omitted (e.g. still loading), renders the
   * default Person icon — never initials.
   */
  person?: WebAppPersonDto;
  /** Used for alt text and initials when the person has no profile image. */
  fallbackName: string;
  /**
   * Fixed pixel size. Omit when nested as a `Chip` `avatar` so MUI Chip styles
   * control size and margins (see Chip avatar adornment).
   */
  size?: PersonAvatarSize;
  /** Forwarded so MUI Chip can apply `MuiChip-avatar` sizing/margin classes. */
  className?: string;
  sx?: AvatarProps['sx'];
};

/**
 * Builds up to two initials from a display name (first + last word).
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * MUI Avatar for a Person: profile image when available, initials when the
 * person is known but has no image, Person icon while person data is missing.
 */
export function PersonAvatar({ person, fallbackName, size, className, sx }: PersonAvatarProps) {
  const px = size ? SIZE_PX[size] : undefined;
  const name = person?.name || fallbackName;
  const src = person?.profileImageUrl ?? undefined;

  return (
    <Avatar
      className={className}
      src={src}
      alt={name}
      sx={{
        ...(px != null ? { width: px, height: px } : null),
        ...(size === 'small' ? { typography: 'caption' } : size ? { typography: 'body2' } : null),
        bgcolor: 'grey.400',
        ...sx,
      }}
    >
      {person ? getInitials(name) : <PersonIcon fontSize="inherit" />}
    </Avatar>
  );
}
